// Wraps livekit-client's Room construction with the mode-aware simulcast
// config. Returns the Room instance (not yet connected — caller does
// room.connect(url, token) after wiring listeners). Browser-only — never
// imported from a server-side function.
//
// Mode-driven simulcast: dm publishes 1 layer, grid 2, stage 3. Publishing
// layers nobody subscribes to is pure upload + CPU waste, especially in DMs.
import { getRoomMode } from './meetUtils.js'

const layersForMode = (VideoPresets, mode) => {
  if (mode === 'dm') return [VideoPresets.h720]
  if (mode === 'grid') return [VideoPresets.h360, VideoPresets.h720]
  return [VideoPresets.h180, VideoPresets.h360, VideoPresets.h720] // stage
}

// Pre-acquire mic+cam BEFORE the room connects — one combined getUserMedia
// (a single permission prompt) kicked off in parallel with the token mint by
// connectCall. Capture options mirror the Room defaults below; publish
// options come from the Room's publishDefaults when connectCall publishes
// these tracks post-connect. Rejects when a device is missing or permission
// is denied — the caller falls back to the per-device
// setMicrophoneEnabled/setCameraEnabled path with its status taxonomy.
// mic/cam prefs come from the join-preview confirm (MEET-AUTOJOIN-1) —
// a kind the user switched OFF is never acquired, so no permission prompt
// and no device light for it. Both off → no getUserMedia at all.
export const preacquireLocalTracks = async ({ mic = true, cam = true } = {}) => {
  if (!mic && !cam) return []
  const { createLocalTracks, VideoPresets } = await import('livekit-client')
  return createLocalTracks({
    audio: mic,
    video: cam ? { resolution: VideoPresets.h720.resolution } : false
  })
}

export const buildLivekitRoom = async (initialParticipantCount = 1) => {
  // Lazy-load livekit-client (WebRTC, ~250KB gz) so it stays OUT of the eager
  // workspace entry — it loads only when a user actually joins a call.
  const { Room, VideoPresets } = await import('livekit-client')
  const mode = getRoomMode(initialParticipantCount)
  return new Room({
    adaptiveStream: true,
    dynacast: true,
    videoCaptureDefaults: { resolution: VideoPresets.h720.resolution },
    publishDefaults: {
      simulcast: mode !== 'dm',
      videoSimulcastLayers: layersForMode(VideoPresets, mode)
    }
  })
}

// Read the HTTP status LiveKit's edge answered a failed join with —
// MEET-CALL-SERVER-UNREACHABLE-1.
//
// ⚠️ WHY THIS EXISTS AT ALL. livekit-client 2.21.0 makes this exact request
// itself (`SignalClient.handleConnectionError` → `createValidateUrl`) and
// then DISCARDS the status for everything except 404/401/403: for any other
// status it returns the inner `ConnectionError.websocket(...)`, which has no
// `status`. Measured in the browser 2026-08-21 against a project whose quota
// was exhausted — the edge answered 429 "bandwidth usage exceeded" on the
// primary host and six regional fallbacks, and the error that reached the
// app was a bare `ServerUnreachable` with `status: undefined`. Without this
// probe the app cannot tell a capacity refusal from an offline edge, so it
// tells the user "hiccup" and offers a Retry that cannot work.
//
// Contract: called ONCE, only AFTER a join already failed, never on the
// happy path — the join cost is unchanged. Returns the status or `null`;
// it never throws and never invents a value. The caller decides which
// statuses it trusts (connectCall adopts 429/401/403 only — a 404 here just
// means the room is not live yet, which is not a failure cause).
//
// `_fetch` is injected for tests; the global is read in exactly this one
// place in the meet package.
export const probeLivekitStatus = async (livekitUrl, token, _fetch) => {
  const f = _fetch || (typeof window !== 'undefined' ? window.fetch.bind(window) : null)
  if (!f || !livekitUrl || !token) return null
  let url
  try {
    const u = new URL(livekitUrl)
    // The dial URL is a websocket URL; the validate endpoint is its HTTP twin.
    u.protocol = u.protocol === 'ws:' ? 'http:' : 'https:'
    u.pathname = `${u.pathname.replace(/\/+$/, '')}/rtc/validate`
    u.search = ''
    u.searchParams.set('access_token', token)
    url = u.toString()
  } catch (_) {
    return null
  }
  try {
    const res = await f(url)
    return typeof res?.status === 'number' ? res.status : null
  } catch (_) {
    // The probe itself could not reach the host. That is genuine
    // unreachability and carries no status — say nothing rather than guess.
    return null
  }
}
